/**
 * DurationHistogram — the first concrete step toward the HSMM (§7) without
 * requiring a trained model or labeled data.
 *
 * The gap: the current engine uses hardcoded dwell thresholds
 * (minObserveDurationMs, debounce). The HSMM's value is an explicit,
 * data-driven duration distribution p(d | state). We cannot train that today,
 * but we CAN start collecting the empirical dwell-time distribution per state
 * from live/recorded sessions. Those quantiles let us replace hand-picked
 * thresholds with data-derived ones (hsmm.md §10, "현실적 중간안 ①") and, later,
 * seed the HSMM's duration priors.
 *
 * Pure logic, no DOM. StateDwellTracker turns a (t, state) stream into
 * completed dwell segments; DurationHistogram accumulates them.
 */

import type { StateId } from '../types';

export interface DurationStats {
  count: number;
  meanMs: number;
  p50: number;
  p90: number;
  minMs: number;
  maxMs: number;
}

export class DurationHistogram {
  private samples = new Map<string, number[]>();

  /** Record one completed dwell of `durationMs` in `state`. */
  record(state: string, durationMs: number): void {
    if (!(durationMs >= 0) || !Number.isFinite(durationMs)) return;
    const arr = this.samples.get(state);
    if (arr) arr.push(durationMs);
    else this.samples.set(state, [durationMs]);
  }

  /** Empirical quantile (linear interpolation) of dwell durations for a state. */
  quantile(state: string, q: number): number | null {
    const arr = this.samples.get(state);
    if (!arr || arr.length === 0) return null;
    const sorted = [...arr].sort((a, b) => a - b);
    const qq = q < 0 ? 0 : q > 1 ? 1 : q;
    const idx = qq * (sorted.length - 1);
    const lo = Math.floor(idx);
    const hi = Math.ceil(idx);
    if (lo === hi) return sorted[lo];
    return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
  }

  statsFor(state: string): DurationStats | null {
    const arr = this.samples.get(state);
    if (!arr || arr.length === 0) return null;
    const sorted = [...arr].sort((a, b) => a - b);
    const sum = sorted.reduce((s, v) => s + v, 0);
    return {
      count: sorted.length,
      meanMs: sum / sorted.length,
      p50: this.quantile(state, 0.5)!,
      p90: this.quantile(state, 0.9)!,
      minMs: sorted[0],
      maxMs: sorted[sorted.length - 1],
    };
  }

  /** Summary across all observed states. */
  summary(): Record<string, DurationStats> {
    const out: Record<string, DurationStats> = {};
    this.samples.forEach((_, state) => {
      const st = this.statsFor(state);
      if (st) out[state] = st;
    });
    return out;
  }

  reset(): void {
    this.samples.clear();
  }
}

/**
 * Turns a stream of (t, state) observations into completed dwell segments.
 * Feed each frame's timestamp + current state; when the state changes, the
 * previous dwell is emitted to the histogram.
 */
export class StateDwellTracker {
  private curState: StateId | null = null;
  private enteredT = 0;
  private lastT = 0;

  constructor(private hist: DurationHistogram) {}

  /** Push one observation. Returns the completed dwell (if state just changed). */
  push(t: number, state: StateId): { state: StateId; durationMs: number } | null {
    let completed: { state: StateId; durationMs: number } | null = null;
    if (this.curState === null) {
      this.curState = state;
      this.enteredT = t;
    } else if (state !== this.curState) {
      const durationMs = t - this.enteredT;
      this.hist.record(this.curState, durationMs);
      completed = { state: this.curState, durationMs };
      this.curState = state;
      this.enteredT = t;
    }
    this.lastT = t;
    return completed;
  }

  /** Flush the in-progress dwell (e.g. at session end). */
  flush(): { state: StateId; durationMs: number } | null {
    if (this.curState === null) return null;
    const durationMs = this.lastT - this.enteredT;
    this.hist.record(this.curState, durationMs);
    const out = { state: this.curState, durationMs };
    this.curState = null;
    return out;
  }
}
